import React, { useEffect, useState } from 'react'
import { FaHistory, FaTshirt, FaBoxes, FaReceipt, FaCog, FaUserShield } from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, EmptyState } from '../components/ui'
import { relativeTime, formatDateTime } from '../lib/format'
import { SHOW_STOCK } from '../lib/features'
import * as api from '../lib/api'

/**
 * Who changed what.
 *
 * The log is append-only at the database level — there is no update or delete
 * policy on activity_log for anyone, including owners. An audit trail that can be
 * rewritten is not an audit trail, and with several people sharing a shop account
 * "who marked this as shipped" is a question that gets asked for real.
 */

// Icon chosen from the verb's prefix, so a new action type added later still gets
// a sensible icon rather than crashing on a missing lookup.
const ICONS = {
  product: FaTshirt,
  inventory: FaBoxes,
  order: FaReceipt,
  settings: FaCog,
  admin: FaUserShield,
}

const iconFor = (action) => ICONS[String(action).split('.')[0]] ?? FaHistory

const ActivityPage = () => {
  const [entries, setEntries] = useState([])
  const [state, setState] = useState({ loading: true, error: null })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const rows = await api.getActivity({ limit: 100 })
        if (!cancelled) {
          // Stock adjustments are filtered out of the view, not out of the log:
          // the rows stay in the database and come back when stock does. Every
          // one of them predates the day stock was switched off, so the log
          // reads as a continuous history either way.
          setEntries((rows ?? []).filter((e) => SHOW_STOCK || !String(e.action).startsWith('inventory')))
          setState({ loading: false, error: null })
        }
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (state.loading) return <Spinner label="Loading activity…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load the activity log.</strong> {state.error.message}</Banner>
  }

  return (
    <div>
      <PageHeader title="Activity" subtitle="Every change made in the panel, newest first" />

      <Card bodyClassName="">
        {entries.length === 0 ? (
          <EmptyState icon={FaHistory} title="Nothing logged yet">
            Catalogue edits{SHOW_STOCK ? ', stock adjustments' : ''} and order changes all appear here.
          </EmptyState>
        ) : (
          <ul className="divide-y divide-hairline/70">
            {entries.map((entry) => {
              const Icon = iconFor(entry.action)
              return (
                <li key={entry.id} className="flex items-start gap-3 px-4 py-3">
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-plane text-ink-muted">
                    <Icon className="text-[11px]" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-ink">{entry.summary ?? entry.action}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-ink-muted">
                      <span className="font-mono">{entry.action}</span>
                      {entry.actor_email && <span>· {entry.actor_email}</span>}
                      <span title={formatDateTime(entry.created_at)}>· {relativeTime(entry.created_at)}</span>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>
    </div>
  )
}

export default ActivityPage
